import { createFileRoute, useRouteContext, useRouter } from "@tanstack/react-router";
import type { JSX } from "react";
import { z } from "zod";

import { cn } from "@repo/cn";
import { graphql } from "@repo/graphql-types";
import {
  BlogListQueryDocument,
  BlogPageQueryDocument,
} from "@repo/graphql-types/graphql";
import Left from "@repo/icons/default/Left";
import Trash from "@repo/icons/default/Trash";

import { BlogPostPreview } from "@/components/blog/blog-post-preview.tsx";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { ConfirmationDrawer } from "@/components/ui/drawer/confirmation-drawer";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page, PageDescription, PageTitle } from "@/components/ui/layout/page/page";
import {
  useGraphQL,
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";

const BlogPostPageQuery = graphql(/* GraphQL */ `
  query BlogPostPageQuery($userPostId: Int!, $userId: Int!) {
    membro {
      publicacoesBlog(where: { idUsuarioPosts: { eq: $userPostId } }) {
        nodes {
          conteudo
          dataCadastro
          publicado
          titulo
        }
      }
    }
    ...BlogPostPreviewFragment
  }
`);

const DropBlogPostMutation = graphql(/* GraphQL */ `
  mutation DropBlogPostMutation($userPostId: Int!) {
    criarOuAlterarPublicacaoBlog(input: { ativo: false, idUsuarioPosts: $userPostId }) {
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
      publicacaoBlogGraphQLType {
        idUsuarioPosts
      }
    }
  }
`);

const BlogPostPage = (): JSX.Element => {
  const { userPostId } = Route.useParams();
  const { user } = useRouteContext({ from: "__root__" });
  const queryResult = useGraphQL(BlogPostPageQuery, {
    userPostId,
    userId: user.codUsuario,
  });

  const router = useRouter();

  const { data } = queryResult;
  const post = data?.membro?.publicacoesBlog?.nodes?.[0];

  const { mutateAsync, isPending } =
    useGraphQLMutationWithErrorHandler(DropBlogPostMutation);
  const invalidateBlogPage = useInvalidateQuery(BlogPageQueryDocument);
  const invalidateBlogList = useInvalidateQuery(BlogListQueryDocument);

  const drawerAction = "drop-blog-post";
  const handleOpenConfirmationDrawer = (): void => {
    void router.navigate({ to: ".", search: { action: drawerAction } });
  };

  const dropBlogPost = (): void => {
    const onSuccess = () => {
      invalidateBlogList();
      invalidateBlogPage();
      router.history.go(-2);
    };

    void mutateAsync(
      {
        userPostId,
      },
      { onSuccess },
    );
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Detalhes do conteúdo" />
        <HeaderButton icon={Trash} align="end" onClick={handleOpenConfirmationDrawer} />
      </HeaderRoot>
      <Page>
        <span
          className={cn("bg-success-100 px-2 py-1 text-xs rounded-xs font-medium", {
            "bg-neutral-100 text-neutral-500": !post?.publicado,
          })}
        >
          {post?.publicado ? "Publicado" : "Rascunho"}
        </span>
        <PageTitle>Prévia do seu conteúdo</PageTitle>
        <PageDescription>
          É assim que seu conteúdo será exibida para os pacientes que acessarem seu site.
        </PageDescription>
        <AsyncDataWrapper {...queryResult}>
          {data ? <BlogPostPreview data={data} /> : null}
        </AsyncDataWrapper>
        <ConfirmationDrawer
          title="Excluir publicação"
          message="Tem certeza que deseja excluir essa publicação?"
          onConfirm={dropBlogPost}
          Icon={Trash}
          confirmText="Excluir"
          openParam={drawerAction}
          isPending={isPending}
        />
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/blog/$userPostId/")({
  component: BlogPostPage,
  parseParams: (params) => ({
    userPostId: z.int().parse(Number(params.userPostId)),
  }),
});
